import React from 'react';
import {
  CalendarDays,
  CircleUserRound,
  ChevronRight,
  ListChecks,
  MessageCircleMore,
  MessageSquare,
  Settings,
  UserRound,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import NewStaffBadge from '../../components/NewStaffBadge';
import { useNewStaff } from '../../utils/useNewStaff';
import UnreadMessageBadge from '../../components/UnreadMessageBadge';
import { useUnreadMessageCount } from '../../utils/appBadge';
import HomeHeroCarousel from '../../components/HomeHeroCarousel';
import StoreSocialButtons from '../../components/StoreSocialButtons';
import { useReserveContext } from '../../contexts/useReserveContext';
import { fetchStoresForReserve, type ReserveStore } from '../../utils/reservation/by-Date/fetchStoresForReserve';
import {
  fetchCustomerAppHomeSettings,
} from '../../utils/reservation/fetchCustomerAppHomeSettings';
import { fetchNextReservation } from '../../utils/reservation/fetchNextReservation';
import type { ReservationSummary } from '../../utils/reservation/types';
import { buildHeroSlides, pickPreferredStore } from './homeShared';
import { useI18n } from '../../i18n';

const ReserveTop: React.FC = () => {
  const navigate = useNavigate();
  const unreadMessageCount = useUnreadMessageCount();
  const { t } = useI18n();
  const { storeId, setStoreId, setStoreName } = useReserveContext();
  const hasNewStaff = useNewStaff(storeId);
  const [heroImages, setHeroImages] = React.useState<ReturnType<typeof buildHeroSlides>>([]);
  const [nextReservation, setNextReservation] = React.useState<ReservationSummary | null>(null);
  const [storeContacts, setStoreContacts] = React.useState<ReserveStore[]>([]);

  React.useEffect(() => {
    let isMounted = true;

    const load = async () => {
      try {
        const homeSettings = await fetchCustomerAppHomeSettings().catch((error) => {
          console.error(error);
          return {
            customer_app_common_home_images: [],
          };
        });
        const commonImages = Array.isArray(homeSettings.customer_app_common_home_images)
          ? homeSettings.customer_app_common_home_images
          : [];

        if (!isMounted) return;
        setHeroImages(buildHeroSlides(commonImages));
      } catch (error) {
        console.error(error);
        if (!isMounted) return;
        setHeroImages([]);
      }
    };

    void load();

    return () => {
      isMounted = false;
    };
  }, []);

  React.useEffect(() => {
    let isMounted = true;

    const loadNextReservation = async () => {
      try {
        const next = await fetchNextReservation();
        if (!isMounted) return;
        setNextReservation(next);
        if (!storeId && next?.store_id) {
          setStoreId(next.store_id);
          setStoreName(next.store_name ?? null);
        }
      } catch (error) {
        console.error(error);
        if (!isMounted) return;
        setNextReservation(null);
      }
    };

    void loadNextReservation();

    return () => {
      isMounted = false;
    };
  }, [setStoreId, setStoreName, storeId]);

  React.useEffect(() => {
    let isMounted = true;

    const loadStoreContacts = async () => {
      try {
        const stores = await fetchStoresForReserve();
        if (!isMounted) return;
        setStoreContacts(Array.isArray(stores) ? stores : []);
      } catch (error) {
        console.error(error);
        if (!isMounted) return;
        setStoreContacts([]);
      }
    };

    void loadStoreContacts();

    return () => {
      isMounted = false;
    };
  }, []);

  const nextReservationDate = nextReservation
    ? new Date(`${nextReservation.reservation_date}T00:00:00`).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      weekday: 'short',
    })
    : '';

  const nextReservationService = nextReservation
    ? [
      nextReservation.category_name,
      nextReservation.service_name,
    ].filter(Boolean).join(' / ') || nextReservation.service_name || ''
    : '';

  const storesWithSocialLinks = React.useMemo(
    () =>
      storeContacts.filter((store) =>
        Boolean(store.telegram_url || store.instagram_url || store.tiktok_url || store.messenger_url),
      ),
    [storeContacts],
  );

  const featuredStoreContact = React.useMemo(
    () => pickPreferredStore(storesWithSocialLinks, storeId),
    [storeId, storesWithSocialLinks],
  );

  const homeActions = [
    {
      title: t('nav.booking'),
      icon: CalendarDays,
      onClick: () => navigate('/reserve/booking'),
    },
    {
      title: t('nav.menu'),
      icon: ListChecks,
      onClick: () => navigate('/reserve/menu'),
    },
    {
      title: t('nav.staff'),
      icon: UserRound,
      onClick: () => navigate('/reserve/staff'),
    },
    {
      title: t('nav.myPage'),
      icon: CircleUserRound,
      onClick: () => navigate('/reserve/mypage'),
    },
    {
      title: t('nav.messages'),
      icon: MessageSquare,
      onClick: () => navigate('/messages'),
    },
    {
      title: t('nav.settings'),
      icon: Settings,
      onClick: () => navigate('/settings'),
    },
  ] as const;

  return (
    <div className="space-y-3">
      <HomeHeroCarousel slides={heroImages} fullBleed />

      <div className="space-y-2.5 px-4 pb-2">
        {nextReservation ? (
          <button
            type="button"
            onClick={() => navigate('/reserve/list')}
            className="w-full overflow-hidden rounded-[22px] border border-slate-200/80 bg-white/95 text-left shadow-[0_12px_24px_rgba(15,23,42,0.08)] transition hover:-translate-y-0.5 hover:shadow-[0_16px_30px_rgba(15,23,42,0.12)]"
          >
            <div className="flex items-center gap-3 bg-[linear-gradient(135deg,#ffffff,#f5f7fb)] px-3.5 py-2.5">
              <div className="flex min-w-0 flex-1 items-center gap-2.5">
                <div className="rounded-[18px] bg-slate-900 p-2 text-white shadow-[0_10px_20px_rgba(15,23,42,0.16)]">
                  <CalendarDays className="h-3.5 w-3.5" />
                </div>
                <div className="min-w-0">
                  <div className="text-[9px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                    {t('reserve.upcomingBooking')}
                  </div>
                  <div className="truncate text-[13px] font-semibold text-slate-900">
                    {nextReservationDate} {nextReservation.start_time} - {nextReservation.end_time}
                  </div>
                  <div className="truncate text-[11px] text-slate-500">
                    {[nextReservation.store_name, nextReservation.staff_name || t('reserve.anyone'), nextReservationService]
                      .filter(Boolean)
                      .join(' / ')}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="hidden rounded-full bg-slate-100 px-2 py-1 text-[10px] font-semibold text-slate-600 sm:block">
                  {nextReservation.status}
                </div>
                <ChevronRight className="h-4.5 w-4.5 text-slate-400" />
              </div>
            </div>
          </button>
        ) : null}

        <div className="grid grid-cols-3 gap-2.5">
          {homeActions.map(({ title, icon: Icon, onClick }) => (
            <button
              key={title}
              type="button"
              onClick={onClick}
              className="group flex min-h-[80px] flex-col items-center justify-center rounded-[18px] border border-white/80 bg-white/95 px-2 py-2.5 text-center shadow-[0_12px_24px_rgba(15,23,42,0.07)] transition hover:-translate-y-0.5 hover:shadow-[0_16px_28px_rgba(15,23,42,0.1)]"
            >
              <div className="relative rounded-[14px] border border-slate-200 bg-slate-50 p-2 text-slate-900 transition group-hover:border-slate-300 group-hover:bg-slate-100">
                <Icon className="h-4 w-4" />
                {Icon === MessageSquare ? <UnreadMessageBadge count={unreadMessageCount} /> : null}
                {Icon === UserRound && hasNewStaff ? (
                  <span className="absolute -right-5 -top-2"><NewStaffBadge /></span>
                ) : null}
              </div>
              <div className="mt-1.5 text-[10px] font-semibold leading-[1.2] text-slate-900">
                {title}
              </div>
            </button>
          ))}
        </div>

        {featuredStoreContact ? (
          <section className="rounded-[22px] border border-slate-200/80 bg-white/95 p-4 shadow-[0_12px_24px_rgba(15,23,42,0.08)]">
            <div className="flex items-start gap-3">
              <div className="rounded-[16px] bg-sky-50 p-2.5 text-sky-700">
                <MessageCircleMore className="h-4.5 w-4.5" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                  {t('reserve.officialLinks')}
                </div>
                <div className="mt-1 truncate text-[14px] font-semibold text-slate-900">
                  {featuredStoreContact.name}
                </div>
                {featuredStoreContact.location ? (
                  <div className="mt-1 text-[11px] text-slate-500">
                    {featuredStoreContact.location}
                  </div>
                ) : null}
              </div>
            </div>

            <StoreSocialButtons
              telegramUrl={featuredStoreContact.telegram_url}
              instagramUrl={featuredStoreContact.instagram_url}
              tiktokUrl={featuredStoreContact.tiktok_url}
              messengerUrl={featuredStoreContact.messenger_url}
              socialLayout="menuGrid"
            />
          </section>
        ) : null}
      </div>
    </div>
  );
};

export default ReserveTop;
